<script>
  import { page } from '$app/stores';
  import SearchBar from '$lib/components/SearchBar.svelte';
  import LanguageSwitcher from '$lib/components/LanguageSwitcher.svelte';
  
  // Track active route for navigation highlighting
  $: path = $page.url.pathname;
  
  let showMenu = false;
  let showSearchMobile = false;
  
  function toggleMenu() {
    showMenu = !showMenu;
    if (showMenu) showSearchMobile = false;
  }
  
  function closeMenu() {
    showMenu = false;
  }
  
  function toggleSearchMobile() {
    showSearchMobile = !showSearchMobile;
    if (showSearchMobile) showMenu = false;
  }
  
  // Primary navigation items - simplified and focused
  const primaryNav = [
    { path: '/', label: 'Home', exact: true },
    { path: '/wheel-of-names', label: 'Wheel of Names' },
    { path: '/secret-santa-generator', label: 'Secret Santa' },
    { path: '/random-team-generator', label: 'Team Generator' },
    { path: '/directory', label: 'Tools' }
  ];
  
  // Check if current path matches nav item
  function isActive(navPath, exact = false) {
    if (exact) return path === navPath;
    return path.startsWith(navPath);
  }
</script>

<header class="modern-header">
  <div class="header-container">
    <!-- Logo Section -->
    <div class="logo-section">
      <a href="/" class="logo-link">
        <div class="logo-icon">🎯</div>
        <span class="logo-text">SpinWheelNames</span>
      </a>
    </div>
    
    <!-- Desktop Navigation -->
    <nav class="desktop-nav">
      <ul class="nav-list">
        {#each primaryNav as navItem}
          <li class="nav-item">
            <a 
              href={navItem.path} 
              class="nav-link"
              class:active={isActive(navItem.path, navItem.exact)}
            >
              {navItem.label}
            </a>
          </li>
        {/each}
      </ul>
    </nav>
    
    <!-- Search and Controls -->
    <div class="header-actions">
      <!-- Desktop Search -->
      <div class="search-wrapper desktop-only">
        <SearchBar placeholder="Search tools..." minimal={true} />
      </div>
      
      <!-- Language Switcher -->
      <div class="language-wrapper">
        <LanguageSwitcher />
      </div>
      
      <!-- Mobile Controls -->
      <div class="mobile-controls">
        <button 
          class="icon-button search-button" 
          on:click={toggleSearchMobile} 
          aria-label="Toggle search"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
        </button>
        
        <button 
          class="icon-button menu-button" 
          class:active={showMenu}
          on:click={toggleMenu} 
          aria-label="Toggle menu"
        >
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
        </button>
      </div>
    </div>
  </div>
  
  <!-- Mobile Search Overlay -->
  <div class="mobile-search-overlay" class:active={showSearchMobile}>
    <div class="search-container">
      <SearchBar placeholder="Search tools and guides..." />
      <button class="close-button" on:click={toggleSearchMobile}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
  </div>
  
  <!-- Mobile Navigation -->
  <nav class="mobile-nav" class:active={showMenu}>
    <div class="mobile-nav-content">
      <ul class="mobile-nav-list">
        {#each primaryNav as navItem}
          <li class="mobile-nav-item">
            <a 
              href={navItem.path} 
              class="mobile-nav-link"
              class:active={isActive(navItem.path, navItem.exact)}
              on:click={closeMenu}
            >
              {navItem.label}
            </a>
          </li>
        {/each}
        
        <!-- Additional mobile links -->
        <li class="mobile-nav-divider"></li>
        <li class="mobile-nav-item">
          <a href="/classroom-name-picker" class="mobile-nav-link" on:click={closeMenu}>Classroom Picker</a>
        </li>
        <li class="mobile-nav-item">
          <a href="/contest-winner-picker" class="mobile-nav-link" on:click={closeMenu}>Contest Winner</a>
        </li>
        <li class="mobile-nav-item">
          <a href="/random-decision-maker" class="mobile-nav-link" on:click={closeMenu}>Decision Maker</a>
        </li>
        <li class="mobile-nav-divider"></li>
        <li class="mobile-nav-item">
          <a href="/faq" class="mobile-nav-link" on:click={closeMenu}>FAQ</a>
        </li>
      </ul>
    </div>
  </nav>
  
  <!-- Mobile nav overlay -->
  {#if showMenu}
    <div class="mobile-nav-overlay" on:click={closeMenu}></div>
  {/if}
</header>

<style>
  /* Z-INDEX HIERARCHY:
   * Modals/Popups: 1000+ (WinnerPopup, Secret Santa modal) 
   * Mobile nav: 50
   * Mobile nav overlay: 49
   * Mobile search: 48
   * Header: 10 (sticky nav)
   */
   
  .modern-header {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    position: sticky;
    top: 0;
    z-index: 10;
    transition: all 0.3s ease;
  }
  
  .header-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 1.5rem;
    height: 70px;
    display: grid;
    grid-template-columns: minmax(200px, 1fr) auto minmax(200px, 1fr);
    align-items: center;
    gap: 1rem;
    overflow: hidden; /* Prevent overflow */
    width: 100%; /* Ensure it doesn't exceed viewport */
  }
  
  /* Logo Section */
  .logo-section {
    justify-self: start;
  }
  
  .logo-link {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: inherit;
    transition: transform 0.2s ease;
  }
  
  .logo-link:hover {
    transform: scale(1.02);
  }
  
  .logo-icon {
    font-size: 1.75rem;
    margin-right: 0.75rem;
  }
  
  .logo-text {
    font-size: 1.5rem;
    font-weight: 700;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  /* Desktop Navigation */
  .desktop-nav {
    justify-self: center;
  }
  
  .nav-list {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
    gap: 1.5rem;
    flex-wrap: nowrap;
  }
  
  .nav-item {
    position: relative;
  }
  
  .nav-link {
    display: block;
    padding: 0.75rem 1rem;
    text-decoration: none;
    color: #4a5568;
    font-weight: 500;
    font-size: 0.9rem;
    border-radius: 8px;
    transition: all 0.2s ease;
    position: relative;
    white-space: nowrap;
  }
  
  .nav-link:hover {
    color: #667eea;
    background-color: rgba(102, 126, 234, 0.08);
  }
  
  .nav-link.active {
    color: #667eea;
    background-color: rgba(102, 126, 234, 0.12);
  }
  
  .nav-link.active::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 4px;
    background-color: #667eea;
    border-radius: 50%;
  }
  
  /* Header Actions */
  .header-actions {
    display: flex;
    align-items: center;
    gap: 1rem;
    justify-self: end;
  }
  
  .search-wrapper {
    width: 280px;
  }
  
  .language-wrapper {
    display: flex;
    align-items: center;
  }
  
  .mobile-controls {
    display: none;
    gap: 0.5rem;
  }
  
  .icon-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: none;
    background: none;
    border-radius: 8px;
    cursor: pointer;
    color: #4a5568;
    transition: all 0.2s ease;
  }
  
  .icon-button:hover {
    background-color: rgba(0, 0, 0, 0.05);
    color: #667eea;
  }
  
  /* Hamburger Menu */
  .menu-button {
    flex-direction: column;
    gap: 3px;
  }
  
  .hamburger-line {
    width: 18px;
    height: 2px;
    background-color: currentColor;
    border-radius: 1px;
    transition: all 0.3s ease;
  }
  
  .menu-button.active .hamburger-line:nth-child(1) {
    transform: rotate(45deg) translate(4px, 4px);
  }
  
  .menu-button.active .hamburger-line:nth-child(2) {
    opacity: 0;
  }
  
  .menu-button.active .hamburger-line:nth-child(3) {
    transform: rotate(-45deg) translate(4px, -4px);
  }
  
  /* Mobile Search Overlay */
  .mobile-search-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: white;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    padding: 1rem 1.5rem;
    transform: translateY(-100%);
    transition: transform 0.3s ease;
    z-index: 48;
  }
  
  .mobile-search-overlay.active {
    transform: translateY(0);
  }
  
  .search-container {
    display: flex;
    align-items: center;
    gap: 1rem;
    max-width: 1200px;
    margin: 0 auto;
  }
  
  .close-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: none;
    background: none;
    border-radius: 8px;
    cursor: pointer;
    color: #4a5568;
    flex-shrink: 0;
  }
  
  /* Mobile Navigation */
  .mobile-nav {
    position: fixed;
    top: 0;
    right: -100%;
    width: 100%;
    max-width: 320px;
    height: 100vh;
    background: white;
    transition: right 0.3s ease;
    z-index: 50;
    overflow-y: auto;
  }
  
  .mobile-nav.active {
    right: 0;
  }
  
  .mobile-nav-content {
    padding: 6rem 0 2rem;
  }
  
  .mobile-nav-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  
  .mobile-nav-item {
    margin: 0;
  }
  
  .mobile-nav-link {
    display: block;
    padding: 1rem 1.5rem;
    text-decoration: none;
    color: #4a5568;
    font-weight: 500;
    transition: all 0.2s ease;
    border-left: 3px solid transparent;
  }
  
  .mobile-nav-link:hover {
    color: #667eea;
    background-color: rgba(102, 126, 234, 0.05);
  }
  
  .mobile-nav-link.active {
    color: #667eea;
    background-color: rgba(102, 126, 234, 0.08);
    border-left-color: #667eea;
  }
  
  .mobile-nav-divider {
    height: 1px;
    background-color: rgba(0, 0, 0, 0.08);
    margin: 1rem 1.5rem;
  }
  
  .mobile-nav-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 49;
  }
  
  /* Responsive Design */
  @media (max-width: 1200px) {
    .search-wrapper {
      width: 220px;
    }
    
    .nav-list {
      gap: 1rem;
    }
    
    .nav-link {
      padding: 0.75rem 0.75rem;
    }
  }
  
  @media (max-width: 1024px) {
    .search-wrapper {
      width: 200px;
    }
    
    .nav-list {
      gap: 0.75rem;
    }
    
    .nav-link {
      padding: 0.75rem 0.6rem;
      font-size: 0.85rem;
    }
  }
  
  @media (max-width: 900px) {
    .header-container {
      display: flex;
      justify-content: space-between;
    }
    
    .desktop-nav {
      display: none;
    }
    
    .mobile-controls {
      display: flex;
    }
    
    .header-actions {
      justify-self: auto;
    }
  }
  
  @media (max-width: 768px) {
    .header-container {
      padding: 0 1rem;
      height: 60px;
    }
    
    .desktop-only {
      display: none;
    }
    
    .logo-text {
      font-size: 1.25rem;
    }
    
    .logo-icon {
      font-size: 1.5rem;
      margin-right: 0.5rem;
    }
  }
  
  @media (max-width: 480px) {
    .header-container {
      padding: 0 0.75rem;
    }
    
    .header-actions {
      gap: 0.5rem;
    }
  }
  
  /* Dark mode support */
  @media (prefers-color-scheme: dark) {
    .modern-header {
      background: rgba(26, 32, 44, 0.95);
      border-bottom-color: rgba(255, 255, 255, 0.08);
    }
    
    .nav-link {
      color: #a0aec0;
    }
    
    .nav-link:hover,
    .nav-link.active {
      color: #90cdf4;
    }
    
    .mobile-nav {
      background: #1a202c;
    }
    
    .mobile-nav-link {
      color: #a0aec0;
    }
    
    .mobile-nav-link:hover,
    .mobile-nav-link.active {
      color: #90cdf4;
    }
    
    .mobile-search-overlay {
      background: #1a202c;
      border-bottom-color: rgba(255, 255, 255, 0.08);
    }
    
    .icon-button {
      color: #a0aec0;
    }
    
    .icon-button:hover {
      color: #90cdf4;
      background-color: rgba(255, 255, 255, 0.05);
    }
  }
</style>
